/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import React, { SetStateAction, Dispatch, useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import TextField from '@material-ui/core/TextField';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import {
  ADD_PRODUCT,
  GET_PRODUCTS_BY_BRAND,
} from '../../../../queries/products/product';

const filter = createFilterOptions<IProduct | IAddOption>();

function isAddOption(option: IProduct | IAddOption): option is IAddOption {
  if ((option as IAddOption).label) {
    return true;
  }

  return false;
}

interface IAddOption {
  label: string;
  value: string;
}

interface ISelectBrandsProps {
  brandId: string | null;
  productId: string | null;
  setProductId: Dispatch<SetStateAction<string | null>>;
}

export const SelectProducts: React.FC<ISelectBrandsProps> = ({
  brandId,
  productId,
  setProductId,
}) => {
  const [creatingNewBrand, setCreatingNewBrand] = useState(false);
  const [internalProduct, setInternalProduct] = useState<
    IProduct | IAddOption | null
  >(null);

  const [getProducts, queryProducts] = useLazyQuery(GET_PRODUCTS_BY_BRAND, {
    fetchPolicy: 'network-only',
  });
  const [addProductMutation] = useMutation(ADD_PRODUCT);

  useEffect(() => {
    if (brandId) {
      getProducts({ variables: { brandId } });
    }
  }, [brandId]);

  useEffect(() => {
    if (brandId) {
      const foundProduct =
        queryProducts?.data?.listProductsByBrand?.find(
          (productOnList: IBrand) => productOnList.id === productId,
        ) || null;
      setInternalProduct(foundProduct);
    }
  }, [productId, queryProducts]);

  const addProduct = async (productName: string) => {
    setCreatingNewBrand(true);
    const result = await addProductMutation({
      variables: {
        brandId,
        name: productName,
      },
    });

    if (result?.data?.createProduct) {
      setProductId(result.data.createProduct?.id);
      queryProducts.refetch!();
    }
    setCreatingNewBrand(false);
  };

  const handleChange = (
    _: unknown,
    newValue: string | IAddOption | IProduct | null,
  ) => {
    if (newValue) {
      if (typeof newValue === 'string') {
        addProduct(newValue);
        setProductId(null);
      } else if (isAddOption(newValue)) {
        addProduct(newValue.value);
        setProductId(null);
      } else {
        setProductId(newValue.id);
      }
    }
  };

  const handleChangeInput = (_: unknown, newValue: string) => {
    const product = queryProducts?.data?.listProductsByBrand?.find(
      (product: IProduct) => newValue === product.name,
    );
    if (product) setProductId(product.id);
  };

  if (queryProducts.loading) return <>Loading...</>;
  if (queryProducts.error) {
    return <>{`Error! ${queryProducts.error.message}`}</>;
  }

  return (
    <>
      <Autocomplete
        value={internalProduct}
        onChange={handleChange}
        onInputChange={handleChangeInput}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (
            params.inputValue !== '' &&
            !options.some((option) => {
              if (isAddOption(option)) return false;
              return option.name === params.inputValue;
            })
          ) {
            filtered.push({
              label: `Add "${params.inputValue}"`,
              value: params.inputValue,
            });
          }

          return filtered;
        }}
        disabled={creatingNewBrand || !brandId}
        options={
          (queryProducts?.data?.listProductsByBrand as (
            | IProduct
            | IAddOption
          )[]) || []
        }
        getOptionLabel={(option) => {
          if (option !== null && option !== undefined) {
            if (isAddOption(option)) {
              return option.label;
            }
            return option.name;
          }
          return '';
        }}
        renderInput={(params) => <TextField {...params} label="Product" />}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
      />
    </>
  );
};
